/**
 * CopyButton - UI Layer
 * Boton tipo chip que muestra el valor truncado + icono de copy.
 * Al hacer click copia el valor completo al clipboard y cambia el icono
 * a un check verde por 1.5s.
 */

import { useState, useCallback } from 'react';
import { Copy, Check } from 'lucide-react';

interface CopyButtonProps {
  /** Valor completo que se copia al clipboard. */
  value: string;
  /** Etiqueta para el tooltip (ej. "Offer ID"). */
  label: string;
  /** Cuantos caracteres mostrar antes de "...". Default: 8. */
  truncateAt?: number;
  /** Variante visual. Default: chip con borde. */
  variant?: 'chip' | 'plain';
}

export function CopyButton({
  value,
  label,
  truncateAt = 8,
  variant = 'chip',
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [value]);

  const display = value.length > truncateAt ? `${value.slice(0, truncateAt)}...` : value;
  const Icon = copied ? Check : Copy;
  const iconColor = copied ? 'text-green-600' : 'text-muted-foreground';

  if (variant === 'plain') {
    return (
      <button
        type="button"
        onClick={handleCopy}
        title={copied ? 'Copiado' : `Copiar ${label}`}
        className="inline-flex items-center gap-1 text-sm font-medium tabular-nums text-foreground transition-colors hover:text-primary"
      >
        <span className="font-mono">{display}</span>
        <Icon size={12} className={iconColor} />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copiado' : `Copiar ${label}`}
      className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/50 px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <Icon size={12} className={iconColor} />
      <span className="font-mono">{display}</span>
    </button>
  );
}
